/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, Component, ErrorInfo, ReactNode } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  BookOpen, 
  PenTool, 
  CheckCircle2, 
  ChevronRight, 
  ChevronLeft, 
  Heart, 
  Calendar,
  User,
  MessageSquareQuote,
  AlertCircle,
  FileDown,
  Play
} from 'lucide-react';
import { JOURNEY_TEXT } from './components/JourneyContent';
import SignaturePad from './components/SignaturePad';
import AdminDashboard from './components/AdminDashboard';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { db, auth, initFirebase, collection, addDoc, serverTimestamp, signInAnonymously } from './firebase';

// Error Boundary Component
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean, errorMessage: string }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, errorMessage: error.message || '發生未知錯誤' };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-rose-50 flex items-center justify-center p-6">
          <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center space-y-4 border border-rose-100">
            <AlertCircle className="w-16 h-16 text-rose-500 mx-auto" />
            <h2 className="text-2xl font-bold text-gray-900">抱歉，出錯了</h2>
            <p className="text-gray-600">系統遇到了一些問題，請重新整理頁面再試一次。</p>
            <div className="p-3 bg-rose-50 rounded-lg text-xs text-rose-700 font-mono break-all">
              {this.state.errorMessage}
            </div>
            <button 
              onClick={() => window.location.reload()}
              className="w-full py-3 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors"
            >
              重新整理頁面
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppContent() {
  const [step, setStep] = useState(0);
  const [isAdminView, setIsAdminView] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    reflections: ['', '', '', ''],
    parentName: '',
    childName: '',
    startDate: '',
    endDate: '',
    signature: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const previewRef = React.useRef<HTMLDivElement>(null);

  // Initialize Firebase on mount
  useEffect(() => {
    initFirebase();
    // Sign in anonymously for parents by default
    if (!auth.currentUser) {
      signInAnonymously(auth).catch(err => console.error("Anon sign in failed", err));
    }
  }, []);

  // Auto-calculate end date (90 days after start)
  useEffect(() => {
    if (formData.startDate) {
      const start = new Date(formData.startDate);
      const end = new Date(start);
      end.setDate(start.getDate() + 90);
      setFormData(prev => ({ ...prev, endDate: end.toISOString().split('T')[0] }));
    }
  }, [formData.startDate]);

  const handleReflectionChange = (index: number, value: string) => {
    const newReflections = [...formData.reflections];
    newReflections[index] = value;
    setFormData(prev => ({ ...prev, reflections: newReflections }));
  };

  const nextStep = () => setStep(prev => prev + 1);
  const prevStep = () => setStep(prev => prev - 1);

  const handlePrint = () => {
    window.print();
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!formData.signature || !formData.parentName || !formData.childName) {
      alert('請填寫完整資訊並完成簽名');
      return;
    }

    setIsSubmitting(true);
    try {
      // Ensure we have a user ID
      let currentUid = auth.currentUser?.uid;
      if (!currentUid) {
        const anonUser = await signInAnonymously(auth);
        currentUid = anonUser.user.uid;
      }

      const path = 'commitments';
      await addDoc(collection(db, path), {
        ...formData,
        createdAt: serverTimestamp(),
        uid: currentUid
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Submission failed:', error);
      // Construct the error info for the agent as per instructions
      const errInfo = {
        error: error instanceof Error ? error.message : String(error),
        operationType: 'create',
        path: 'commitments',
        authInfo: {
          userId: auth.currentUser?.uid,
          email: auth.currentUser?.email,
          emailVerified: auth.currentUser?.emailVerified,
          isAnonymous: auth.currentUser?.isAnonymous,
          tenantId: auth.currentUser?.tenantId,
          providerInfo: auth.currentUser?.providerData.map(p => ({
            providerId: p.providerId,
            displayName: p.displayName,
            email: p.email,
            photoUrl: p.photoURL
          })) || []
        }
      };
      throw new Error(JSON.stringify(errInfo));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isAdminView) {
    return (
      <div className="relative">
        <button 
          onClick={() => setIsAdminView(false)}
          className="fixed top-4 right-4 z-50 bg-white/80 backdrop-blur p-2 rounded-full shadow-lg hover:bg-white transition-all"
          title="返回家長頁面"
        >
          <ChevronLeft size={24} className="text-amber-900" />
        </button>
        <AdminDashboard />
      </div>
    );
  }

  const renderStep = () => {
    switch (step) {
      case 0: // Welcome & Preface
        return (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            <div className="text-center space-y-4">
              <h1 className="text-3xl md:text-4xl font-bold text-amber-900">90天禱告旅程</h1>
              <p className="text-amber-700 italic">「父母的話語能夠賦予孩子能力、勇氣和智慧。」</p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 md:p-10 shadow-xl border border-amber-100">
              <div className="flex items-center gap-3 mb-6 text-amber-800">
                <BookOpen className="w-6 h-6" />
                <h2 className="text-xl font-semibold">{JOURNEY_TEXT.preface.title}</h2>
              </div>
              <div className="prose prose-amber max-w-none space-y-6 text-xl md:text-2xl text-gray-800 leading-relaxed font-medium">
                {JOURNEY_TEXT.preface.content.map((para, i) => (
                   <p key={i}>{para}</p>
                ))}
              </div>

              {/* YouTube Embedded Video Section */}
              <div className="mt-10 space-y-4">
                <h3 className="text-lg font-bold text-amber-900 flex items-center gap-2">
                  <Play size={20} className="fill-amber-600 text-amber-600" /> 前導說明影片
                </h3>
                <div className="relative overflow-hidden rounded-2xl shadow-xl border-4 border-white aspect-video bg-gray-900">
                  <iframe
                    src="https://www.youtube.com/embed/diKqllJyHlY"
                    title="90天祝福禱告旅程前導說明"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  ></iframe>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-amber-100">
                <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed">
                  如果你好奇的拿起這本書想翻翻看，我相信這一定不是偶然，也相信你一定很愛你的孩子。
                  你願意加入我，為你自己、也為你的孩子開始這趟祝福禱告九十天的奇妙旅程嗎？
                </p>
                <button 
                  onClick={nextStep}
                  className="w-full py-5 bg-amber-600 hover:bg-amber-700 text-white rounded-2xl font-bold text-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-200"
                >
                  開始這趟旅程 <ChevronRight size={24} />
                </button>
              </div>
            </div>
          </motion.div>
        );

      case 1: // Core Concepts
        return (
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-8"
          >
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-amber-900">中心觀念</h2>
              <p className="text-amber-700">在開始之前，讓我們持守這些信念</p>
            </div>

            <div className="grid gap-6">
              {JOURNEY_TEXT.concepts.map((concept, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white/90 p-6 rounded-2xl shadow-md border border-amber-50 flex gap-4 items-start"
                >
                  <div className="p-4 bg-white rounded-2xl shadow-sm">
                    {React.cloneElement(concept.icon as React.ReactElement<{ size?: number }>, { size: 32 })}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-amber-900 mb-2">{concept.title}</h3>
                    <p className="text-gray-700 text-lg leading-relaxed">{concept.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="flex gap-4">
              <button onClick={prevStep} className="flex-1 py-3 border-2 border-amber-200 text-amber-800 rounded-xl font-medium hover:bg-amber-50 transition-colors">上一步</button>
              <button onClick={nextStep} className="flex-[2] py-3 bg-amber-600 text-white rounded-xl font-semibold hover:bg-amber-700 transition-all shadow-lg shadow-amber-200">繼續下一步</button>
            </div>
          </motion.div>
        );

      case 2: // Reflections
        return (
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-8"
          >
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-amber-900">思想與記錄</h2>
              <p className="text-amber-700">請在開始這趟旅程前，記錄下您的期待</p>
            </div>

            <div className="space-y-8">
              {JOURNEY_TEXT.questions.map((q, i) => (
                <div key={i} className="space-y-4">
                  <label className="block text-amber-900 font-bold text-xl md:text-2xl leading-snug">
                    {i + 1}. {q}
                  </label>
                  <textarea 
                    value={formData.reflections[i]}
                    onChange={(e) => handleReflectionChange(i, e.target.value)}
                    className="w-full p-6 rounded-2xl border-2 border-amber-100 focus:border-amber-400 focus:ring-0 bg-white/50 min-h-[150px] text-lg transition-all"
                    placeholder="請輸入您的心得或期待..."
                  />
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <button onClick={prevStep} className="flex-1 py-3 border-2 border-amber-200 text-amber-800 rounded-xl font-medium hover:bg-amber-50 transition-colors">上一步</button>
              <button onClick={nextStep} className="flex-[2] py-3 bg-amber-600 text-white rounded-xl font-semibold hover:bg-amber-700 transition-all shadow-lg shadow-amber-200">填寫承諾書</button>
            </div>
          </motion.div>
        );

      case 3: // Commitment & Signature
        return (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="space-y-8"
          >
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-amber-900">旅程約定</h2>
              <p className="text-amber-700">帶著一顆期待的心，為孩子獻上感恩</p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white/90 p-6 md:p-10 rounded-3xl shadow-2xl border-2 border-amber-100 space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-amber-900 font-bold text-lg">
                    <User size={20} /> 家長姓名
                  </label>
                  <input 
                    type="text" 
                    required
                    value={formData.parentName}
                    onChange={(e) => setFormData(prev => ({ ...prev, parentName: e.target.value }))}
                    className="w-full p-4 rounded-xl border border-amber-200 focus:border-amber-500 outline-none text-lg"
                    placeholder="請輸入您的姓名"
                  />
                </div>
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-amber-900 font-bold text-lg">
                    <Heart size={20} /> 孩子姓名
                  </label>
                  <input 
                    type="text" 
                    required
                    value={formData.childName}
                    onChange={(e) => setFormData(prev => ({ ...prev, childName: e.target.value }))}
                    className="w-full p-4 rounded-xl border border-amber-200 focus:border-amber-500 outline-none text-lg"
                    placeholder="請輸入孩子的姓名"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-amber-900 font-bold text-lg">
                    <Calendar size={20} /> 開始日期
                  </label>
                  <input 
                    type="date" 
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full p-4 rounded-xl border border-amber-200 focus:border-amber-500 outline-none text-lg"
                  />
                </div>
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-amber-900 font-bold text-lg">
                    <Calendar size={20} /> 預計完成日期
                  </label>
                  <input 
                    type="date" 
                    disabled
                    value={formData.endDate}
                    className="w-full p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-700 outline-none text-lg"
                  />
                </div>
              </div>

              <div className="space-y-6 pt-4">
                <div className="flex items-start gap-4 text-amber-900 text-lg md:text-xl leading-relaxed">
                  <CheckCircle2 className="mt-1 flex-shrink-0 text-amber-600" size={24} />
                  <p>願意為我的孩子連續祝福禱告九十天。</p>
                </div>
                <div className="flex items-start gap-4 text-amber-900 text-lg md:text-xl leading-relaxed">
                  <CheckCircle2 className="mt-1 flex-shrink-0 text-amber-600" size={24} />
                  <p>願意每天開口揚聲說出禱告文，也願意花時間用自己的話，祝福自己的孩子。</p>
                </div>
              </div>

              <div className="space-y-4 pt-6">
                <label className="flex items-center gap-2 text-amber-900 font-bold text-xl">
                  <PenTool size={24} /> 電子簽名
                </label>
                <SignaturePad 
                  onSave={(data) => setFormData(prev => ({ ...prev, signature: data }))}
                  onClear={() => setFormData(prev => ({ ...prev, signature: '' }))}
                />
                {formData.signature && (
                  <div className="mt-2 p-2 border border-amber-100 rounded-lg bg-amber-50/50 flex flex-col items-center">
                    <p className="text-xs text-amber-600 mb-1">簽名預覽：</p>
                    <img src={formData.signature} alt="Preview" className="max-h-20 object-contain" />
                  </div>
                )}
              </div>

              <div className="flex gap-4 pt-6">
                <button type="button" onClick={prevStep} className="flex-1 py-4 border-2 border-amber-200 text-amber-800 rounded-xl font-medium hover:bg-amber-50 transition-colors">上一步</button>
                <button 
                  type="button"
                  onClick={nextStep}
                  className="flex-[2] py-4 bg-amber-600 text-white rounded-xl font-bold text-lg hover:bg-amber-700 transition-all shadow-xl shadow-amber-200"
                >
                  預覽約定書
                </button>
              </div>
            </form>
          </motion.div>
        );

      case 4: // Review & PDF Preview
        return (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-amber-900">確認並預覽</h2>
              <p className="text-amber-700">請確認您的約定內容，確認無誤後即可提交</p>
            </div>

            {/* Document Preview */}
            <div className="bg-gray-200 p-4 md:p-8 rounded-3xl overflow-x-auto">
              <div 
                ref={previewRef}
                id="pdf-preview-content"
                className="bg-white mx-auto shadow-2xl p-8 md:p-16 min-h-[800px] w-full max-w-[600px] text-gray-900 space-y-10 font-serif"
              >
                <div className="text-center space-y-4 border-b-2 border-amber-900 pb-8">
                  <h1 className="text-3xl font-bold text-amber-900">90天祝福禱告旅程約定書</h1>
                  <p className="text-gray-500 italic">「父母的話語能夠賦予孩子能力、勇氣和智慧。」</p>
                </div>

                <div className="space-y-6 text-lg leading-relaxed">
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="font-bold text-amber-900">家長姓名：</span>
                    <span>{formData.parentName}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="font-bold text-amber-900">孩子姓名：</span>
                    <span>{formData.childName}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="font-bold text-amber-900">約定期間：</span>
                    <span>{formData.startDate} 至 {formData.endDate}</span>
                  </div>
                </div>

                <div className="space-y-6">
                  <h3 className="font-bold text-amber-900 border-l-4 border-amber-600 pl-3">我的期待與記錄</h3>
                  {formData.reflections.map((ref, i) => (
                    <div key={i} className="space-y-2">
                      <p className="text-sm font-bold text-gray-400">問題 {i + 1}：</p>
                      <p className="text-gray-800 whitespace-pre-wrap pl-4 border-l border-gray-100">{ref || '(未填寫)'}</p>
                    </div>
                  ))}
                </div>

                <div className="space-y-6 pt-8">
                  <div className="flex items-start gap-3 text-gray-700">
                    <div className="w-5 h-5 border-2 border-amber-600 rounded flex-shrink-0 mt-1 flex items-center justify-center">
                      <div className="w-3 h-3 bg-amber-600 rounded-sm" />
                    </div>
                    <p>我願意為我的孩子連續祝福禱告九十天。</p>
                  </div>
                  <div className="flex items-start gap-3 text-gray-700">
                    <div className="w-5 h-5 border-2 border-amber-600 rounded flex-shrink-0 mt-1 flex items-center justify-center">
                      <div className="w-3 h-3 bg-amber-600 rounded-sm" />
                    </div>
                    <p>我願意每天開口揚聲說出禱告文，也願意花時間用自己的話，祝福自己的孩子。</p>
                  </div>
                </div>

                <div className="pt-12 flex flex-col items-end space-y-4">
                  <p className="font-bold text-amber-900">承諾人簽名：</p>
                  <div className="w-48 h-24 border-b-2 border-gray-300 flex items-center justify-center">
                    {formData.signature && (
                      <img src={formData.signature} alt="Signature" className="max-h-full object-contain" />
                    )}
                  </div>
                  <p className="text-sm text-gray-400">日期：{new Date().toLocaleDateString('zh-TW')}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 no-print">
              <button 
                onClick={prevStep} 
                className="flex-1 py-4 border-2 border-amber-200 text-amber-800 rounded-xl font-medium hover:bg-amber-50 transition-colors"
              >
                返回修改
              </button>
              <button 
                onClick={handlePrint}
                className="flex-1 py-4 bg-white border-2 border-rose-200 text-rose-600 rounded-xl font-bold hover:bg-rose-50 transition-all flex items-center justify-center gap-2"
              >
                <FileDown size={20} /> 列印 / 另存 PDF
              </button>
              <button 
                onClick={() => handleSubmit()}
                disabled={isSubmitting}
                className="flex-[2] py-4 bg-amber-600 text-white rounded-xl font-bold text-lg hover:bg-amber-700 transition-all shadow-xl shadow-amber-200 disabled:opacity-50"
              >
                {isSubmitting ? '提交中...' : '確認無誤，正式提交'}
              </button>
            </div>
          </motion.div>
        );
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#fdf8f1] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white p-10 rounded-3xl shadow-2xl text-center space-y-6 border border-amber-100"
        >
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto text-amber-600">
            <CheckCircle2 size={48} />
          </div>
          <h2 className="text-3xl font-bold text-amber-900">約定已提交！</h2>
          <p className="text-gray-600">
            親愛的 {formData.parentName}，感謝您為 {formData.childName} 開啟這趟 90 天的祝福旅程。
            願天父的愛與智慧時刻充滿您的家庭。
          </p>
          <div className="pt-4">
            <div className="text-sm text-amber-700 bg-amber-50 p-4 rounded-xl">
              旅程期間：{formData.startDate} 至 {formData.endDate}
            </div>
          </div>
          <button 
            onClick={() => setIsSubmitted(false)}
            className="w-full py-3 text-amber-600 font-medium hover:underline"
          >
            返回查看內容
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdf8f1] text-gray-800 font-sans selection:bg-amber-200">
      {/* Decorative background elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden no-print">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-amber-100/50 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-rose-100/30 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 py-12 md:py-20">
        <AnimatePresence mode="wait">
          {renderStep()}
        </AnimatePresence>

        {/* Progress indicator */}
        <div className="mt-12 flex justify-center gap-2 no-print">
          {[0, 1, 2, 3, 4].map((i) => (
            <div 
              key={i}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                step === i ? 'w-8 bg-amber-600' : 'w-2 bg-amber-200'
              }`}
            />
          ))}
        </div>

        {/* Admin Link */}
        <div className="mt-20 text-center no-print">
          <button 
            onClick={() => setIsAdminView(true)}
            className="text-xs text-amber-200 hover:text-amber-400 transition-colors"
          >
            管理員登入
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
}
